import React, { useMemo } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  LinearProgress,
  Stack,
  Paper,
  Divider,
} from "@mui/material";
import {
  FolderOpenTwoTone as FolderIcon,
  BookmarkTwoTone as BookmarkIcon,
  DeleteTwoTone as TrashIcon,
  CloudQueue as CloudIcon,
  PersonOutline as PersonIcon,
  PermContactCalendarTwoTone as ContactIcon,
  FolderSharedTwoTone as SharedIcon,
  DraftsTwoTone as DraftIcon,
  MapsHomeWorkTwoTone as HomeIcon,
} from "@mui/icons-material";
import { getMyPaletteColor } from "../utils/colorUtils.ts";
import {
  getBgColor,
  getAccentColor,
  PaletteColor,
} from "../types/PaletteColor.ts";
import { useDarkMode } from "../hooks/useDarkMode.ts";
import { StorageStatusDto } from "./FileManager.tsx";

interface SidebarProps {
  storage?: StorageStatusDto;
  isAdmin: boolean;
  activeItem?: string;
  onItemClick?: (text: string) => void;
  roomId: number;
  roomName: string;
  isMobile?: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({
  storage,
  isAdmin,
  activeItem = "My Room",
  onItemClick,
  roomId,
  isMobile = false,
}) => {
  const paletteColor: PaletteColor = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return getMyPaletteColor(params.get("color"));
  }, []);

  const isDarkMode = useDarkMode();
  const activeBg = getBgColor(paletteColor, isDarkMode);
  const activeText = getAccentColor(paletteColor, isDarkMode);

  // 補助計算関数
  const formatBytes = (bytes: number) => {
    const TB = 1024 * 1024 * 1024 * 1024;
    const GB = 1024 * 1024 * 1024;
    if (bytes >= TB) return (bytes / TB).toFixed(2) + " TB";
    if (bytes >= GB) return (bytes / GB).toFixed(1) + " GB";
    return (bytes / (1024 * 1024)).toFixed(0) + " MB";
  };
  const getPercent = (used: number, max: number) =>
    Math.min(100, (used / (max || 1)) * 100);

  const renderItem = (text: string, icon: React.ReactNode) => {
    const isActive = activeItem === text;
    return (
      <ListItem key={text} disablePadding sx={{ mb: 0.5 }}>
        <ListItemButton
          sx={{
            borderRadius: 2,
            bgcolor: isActive ? activeBg : "transparent",
            color: isActive ? activeText : "text.secondary",
          }}
          onClick={() => onItemClick && onItemClick(text)}
        >
          <ListItemIcon
            sx={{
              color: isActive ? activeText : "inherit",
              minWidth: 40,
            }}
          >
            {icon}
          </ListItemIcon>
          <ListItemText
            primary={text}
            slotProps={{
              primary: { fontSize: "0.9rem", fontWeight: isActive ? 600 : 500 },
            }}
          />
        </ListItemButton>
      </ListItem>
    );
  };

  const renderSectionLabel = (label: string) => (
    <Box sx={{ display: "flex", alignItems: "center", px: 1, mt: 2, mb: 0.5 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ whiteSpace: "nowrap", mr: 1, fontSize: "0.7rem" }}
      >
        {label}
      </Typography>
      <Box
        sx={{ flex: 1, borderBottom: "1px dashed", borderColor: "divider" }}
      />
    </Box>
  );

  return (
    <Box
      sx={{
        width: 320,
        height: "100%",
        borderRight: 1,
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
      }}
    >
      <Box sx={{ flexGrow: 1, px: 2, pt: 2 }}>
        <List disablePadding>
          {!isMobile && roomId > 0 && (
            <>
              {renderSectionLabel("部屋の共有メニュー")}
              {renderItem("部屋の共有フォルダ", <FolderIcon />)}
              {renderItem("ゴミ箱（共有フォルダ）", <TrashIcon />)}
              {renderItem("チャット送信ファイル", <DraftIcon />)}
            </>
          )}
          {!isMobile && (
            <>
              {renderSectionLabel("個人専用のメニュー")}
              {renderItem("マイフォルダ", <ContactIcon />)}
              {renderItem("ゴミ箱（マイフォルダ）", <TrashIcon />)}
            </>
          )}
          {renderItem("ブックマーク", <BookmarkIcon />)}
          {/* モバイル用: ゴミ箱はドロワーに直接表示 */}
          {isMobile &&
            roomId > 0 &&
            renderItem("ゴミ箱（共有フォルダ）", <TrashIcon />)}
          {isMobile && renderItem("ゴミ箱（マイフォルダ）", <TrashIcon />)}
          {isAdmin && (
            <>
              {renderSectionLabel("管理者メニュー")}
              {renderItem("施設管理", <HomeIcon />)}
            </>
          )}
        </List>
      </Box>

      {/* ストレージエリア */}
      <Box sx={{ p: 2 }}>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 3,
            bgcolor: "background.paper",
            borderStyle: "dashed",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {/* 管理者のみ: 施設全体 */}
          {isAdmin && (
            <Box>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 0.5 }}
              >
                <CloudIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                <Typography variant="caption" fontWeight="bold">
                  施設のストレージ容量
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={getPercent(
                  storage?.usedStorageBytes || 0,
                  storage?.maxStorageBytes || 1,
                )}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: "divider",
                  "& .MuiLinearProgress-bar": { bgcolor: "primary.main" },
                  mb: 0.5,
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {formatBytes(storage?.usedStorageBytes || 0)} /{" "}
                {formatBytes(storage?.maxStorageBytes || 0)}
              </Typography>
            </Box>
          )}

          {/* 部屋のストレージ容量 (roomId > 0 の場合) */}
          {storage?.roomUsedBytes != null &&
            storage?.roomQuotaBytes != null && (
              <>
                {isAdmin && <Divider />}
                <Box>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 0.5 }}
                  >
                    <SharedIcon
                      sx={{ fontSize: 16, color: "text.secondary" }}
                    />
                    <Typography variant="caption" fontWeight="bold">
                      部屋のストレージ容量
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={getPercent(
                      storage.roomUsedBytes,
                      storage.roomQuotaBytes,
                    )}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: "divider",
                      "& .MuiLinearProgress-bar": { bgcolor: "info.main" },
                      mb: 0.5,
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {formatBytes(storage.roomUsedBytes)} /{" "}
                    {formatBytes(storage.roomQuotaBytes)}
                  </Typography>
                </Box>
              </>
            )}

          {/* 個人のストレージ容量 */}
          {(isAdmin || storage?.roomUsedBytes != null) && <Divider />}
          <Box>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 0.5 }}
            >
              <PersonIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              <Typography variant="caption" fontWeight="bold">
                個人のストレージ容量
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={getPercent(
                storage?.userUsedBytes || 0,
                storage?.userQuotaBytes || 1,
              )}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: "divider",
                "& .MuiLinearProgress-bar": { bgcolor: "success.main" },
                mb: 0.5,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {formatBytes(storage?.userUsedBytes || 0)} /{" "}
              {formatBytes(storage?.userQuotaBytes || 0)}
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};
